// CreatorProviderByokPanel — Account & Billing.
//
// Feb 27, 2026 — Phase 2A. Lets creators bring their own Anthropic / OpenAI
// keys so non-Gemini calls bypass the platform reservoir entirely and bill
// directly to the creator's provider account. Big win: they pay 1× raw
// invoice instead of the 3× platform markup.
//
// Mirrors the existing Gemini BYOKCard pattern (eye-popup walkthrough,
// console link, encrypted save, secure clear) and stacks both providers
// in a single bordered card so the section reads as one decision: "go
// direct on Claude / GPT or keep using the platform reservoir."

import { useState } from "react";
import { Key, Eye, ArrowSquareOut, X, FloppyDisk, CheckCircle, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, setUser as saveUser } from "@/lib/api";

const PROVIDERS = [
  {
    id: "gemini",
    label: "Google Gemini",
    short: "Gemini 2.5 Pro / Flash / 3.1 — Google's flagship + free-tier capable",
    consoleUrl: "https://aistudio.google.com/app/apikey",
    keyHint: "AIzaSy...",
    walkthrough: [
      "Sign in at aistudio.google.com.",
      "Open Get API key → Create API key. Pick or create a Google Cloud project.",
      "Copy the key and paste it below.",
      "Gemini's free tier is generous — you usually won't need paid billing right away. Add a card if you want the higher rate limits.",
    ],
    field: "has_byok",
    saveEndpoint: "/auth/byok",
    saveBody: (key) => ({ creator_api_key: key || null }),
    modelLabel: "Gemini",
  },
  {
    id: "anthropic",
    label: "Anthropic (Claude)",
    short: "Direct connection to your Anthropic account · pays 1× raw invoice",
    consoleUrl: "https://console.anthropic.com/settings/keys",
    keyHint: "sk-ant-...",
    walkthrough: [
      "Sign in at console.anthropic.com (or create a free account).",
      "Open Settings → API Keys → Create Key. Name it 'My Silo'.",
      "Copy the key (you'll only see it once) and paste it below.",
      "Before saving: top up Anthropic with at least $5 under Settings → Billing. Without a balance, every Claude call on your silos fails.",
      "Enable auto-reload so a card runs dry doesn't take down your silos overnight.",
    ],
    field: "has_anthropic_byok",
    saveEndpoint: "/auth/byok/provider",
    saveBody: (key) => ({ provider: "anthropic", api_key: key || null }),
    modelLabel: "Claude",
  },
  {
    id: "openai",
    label: "OpenAI (GPT · Whisper · Sora)",
    short: "Direct connection to your OpenAI account · pays 1× raw invoice",
    consoleUrl: "https://platform.openai.com/api-keys",
    keyHint: "sk-...",
    walkthrough: [
      "Sign in at platform.openai.com.",
      "Open API keys → Create new secret key. Name it 'My Silo'.",
      "Copy the key and paste it below.",
      "Top up OpenAI with at least $5 under Settings → Billing.",
      "Enable auto-reload.",
    ],
    field: "has_openai_byok",
    saveEndpoint: "/auth/byok/provider",
    saveBody: (key) => ({ provider: "openai", api_key: key || null }),
    modelLabel: "GPT",
  },
];

function WalkthroughModal({ provider, onClose }) {
  if (!provider) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}
      data-testid={`creator-byok-walkthrough-${provider.id}`}>
      <div className="absolute inset-0 bg-[#1C1B1A]/40 backdrop-blur-sm" />
      <div className="relative z-10 bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#7A5200] flex items-center gap-1.5">
              <Key size={12} weight="duotone" /> How to get your key
            </div>
            <h3 className="font-serif-display text-xl text-[#1C1B1A] mt-1">{provider.label}</h3>
            <p className="text-xs text-[#6B6A68] mt-1">{provider.short}</p>
          </div>
          <button onClick={onClose} className="text-[#6B6A68] hover:text-[#1C1B1A] p-1"
            data-testid="creator-byok-walkthrough-close">
            <X size={16} />
          </button>
        </div>

        <ol className="space-y-2 mb-4">
          {provider.walkthrough.map((step, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-[#1C1B1A]">
              <span className="text-[10px] font-medium text-[#7A5200] bg-[#FBE9E1] rounded-full w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
              <span className="leading-relaxed">{step}</span>
            </li>
          ))}
        </ol>

        <a href={provider.consoleUrl} target="_blank" rel="noopener noreferrer"
          data-testid={`creator-byok-walkthrough-open-${provider.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#4A5D4E] text-white text-xs uppercase tracking-[0.15em] hover:bg-[#1C1B1A]">
          <ArrowSquareOut size={12} /> Open {provider.label} console
        </a>
      </div>
    </div>
  );
}

function ProviderRow({ provider, user, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const isSet = !!user?.[provider.field];

  const save = async () => {
    if (!key.trim()) return toast.error("Paste a key first.");
    setSaving(true);
    try {
      const r = await api.patch(provider.saveEndpoint, provider.saveBody(key.trim()));
      saveUser(r.data);
      onUpdated?.(r.data);
      setKey("");
      setEditing(false);
      toast.success(`${provider.label} key saved. Your ${provider.modelLabel} calls now bill directly to your account at 1× cost.`);
      // Feb 28, 2026 — When the backend auto-swept stranded voucher, tell
      // the user where it landed: Stripe Express (instantly withdrawable)
      // or pending-connect (waiting for Stripe Connect to come online).
      const sweptCents = parseInt(r.headers?.["x-voucher-sweep-cents"] || "0", 10);
      const sweptDest = (r.headers?.["x-voucher-sweep-destination"] || "").toLowerCase();
      if (sweptCents > 0) {
        const dollars = (sweptCents / 100).toFixed(2);
        if (sweptDest === "stripe_express") {
          toast.success(
            `+ $${dollars} transferred to your Stripe Express balance — your unused ${provider.modelLabel} voucher. Withdraw to your bank from the Earnings page.`,
            { duration: 9000 }
          );
        } else {
          toast.success(
            `+ $${dollars} held for you — your unused ${provider.modelLabel} voucher. Connect Stripe Express on the Earnings page to release it to your bank.`,
            { duration: 11000 }
          );
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const clearKey = async () => {
    if (!window.confirm(`Remove your ${provider.label} key? Calls will go back through the platform reservoir at 3× markup.`)) return;
    setSaving(true);
    try {
      const r = await api.patch(provider.saveEndpoint, provider.saveBody(null));
      saveUser(r.data);
      onUpdated?.(r.data);
      toast.success("Key removed.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove");
    } finally { setSaving(false); }
  };

  const testKey = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/auth/byok/test", { provider: provider.id });
      setTestResult(r.data);
      if (r.data.ok) toast.success(`${provider.label} key works ✓`);
      else toast.error(r.data.error || "Test failed");
    } catch (err) {
      const e = err.response?.data?.detail || err.response?.data?.error || "Test failed";
      setTestResult({ ok: false, error: e });
      toast.error(e);
    } finally { setTesting(false); }
  };

  return (
    <>
      <div className={`pt-4 first:pt-0 ${isSet ? "border-l-2 border-l-[#4A5D4E] -ml-3 pl-3" : ""}`}
        data-testid={`creator-byok-row-${provider.id}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-[#1C1B1A]">{provider.label}</span>
              {isSet ? (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-[#2F6B3A] bg-[#E8EBE5] px-2 py-0.5 rounded">
                  <CheckCircle size={10} weight="duotone" /> Direct · 1× cost
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-[0.15em] text-[#2F6B3A] bg-[#E8EBE5] px-2 py-0.5 rounded">
                  Complete action for 3× cheaper LLM calls (example = 3¢ → 1¢)
                </span>
              )}
            </div>
            <div className="text-xs text-[#6B6A68] mt-0.5">{provider.short}</div>
          </div>
          <div className="flex gap-2 items-center">
            <button onClick={() => setShowWalkthrough(true)}
              data-testid={`creator-byok-${provider.id}-help-btn`}
              className="text-[11px] px-2 py-1.5 rounded-md text-[#6B6A68] hover:text-[#1C1B1A] hover:bg-[#F0F1EE] inline-flex items-center gap-1">
              <Eye size={12} weight="duotone" /> How
            </button>
            {isSet && !editing && (
              <button onClick={testKey} disabled={saving || testing}
                data-testid={`creator-byok-${provider.id}-test-btn`}
                className="text-xs px-3 py-1.5 rounded-md text-[#4A5D4E] hover:bg-[#F0F1EE] disabled:opacity-50">
                {testing ? "Testing…" : "Test ping"}
              </button>
            )}
            {isSet && !editing && (
              <button onClick={clearKey} disabled={saving}
                data-testid={`creator-byok-${provider.id}-clear-btn`}
                className="text-xs px-3 py-1.5 rounded-md text-[#C16E5A] hover:bg-[#FDF1ED] disabled:opacity-50">
                Remove
              </button>
            )}
            {!editing && (
              <button onClick={() => setEditing(true)}
                data-testid={`creator-byok-${provider.id}-edit-btn`}
                className="text-xs px-3 py-1.5 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E]">
                {isSet ? "Replace" : "Add key"}
              </button>
            )}
          </div>
        </div>

        {editing && (
          <div className="mt-3 flex items-stretch gap-2">
            <div className="flex-1 relative">
              <input
                type={showKey ? "text" : "password"}
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder={`Paste — ${provider.keyHint}`}
                data-testid={`creator-byok-${provider.id}-input`}
                className="w-full bg-white border hairline rounded-md px-3 py-2 pr-10 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                autoFocus
              />
              <button type="button" onClick={() => setShowKey((s) => !s)}
                data-testid={`creator-byok-${provider.id}-toggle-show`}
                className="absolute right-1.5 top-1.5 p-1.5 text-[#6B6A68] hover:text-[#1C1B1A]">
                {showKey ? <EyeSlash size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <button onClick={save} disabled={saving || !key.trim()}
              data-testid={`creator-byok-${provider.id}-save-btn`}
              className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-60 inline-flex items-center gap-1">
              <FloppyDisk size={12} /> {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={() => { setEditing(false); setKey(""); }}
              data-testid={`creator-byok-${provider.id}-cancel-btn`}
              className="text-xs px-3 py-2 text-[#6B6A68] hover:text-[#1C1B1A]">Cancel</button>
          </div>
        )}

        {isSet && !editing && (
          <div className="mt-2 text-[11px] text-[#6B6A68] font-mono bg-[#F0F1EE] inline-block px-2.5 py-1 rounded">••••••••••••••• · stored securely</div>
        )}
        {testResult && (
          <div className={`mt-2 text-[11px] p-2 rounded inline-block ${testResult.ok ? "bg-[#E8EBE5] text-[#2F6B3A]" : "bg-[#FDF1ED] text-[#7A2A20]"}`}
            data-testid={`creator-byok-${provider.id}-test-result`}>
            {testResult.ok ? `Live · ${testResult.model || "ok"}` : `Failed · ${testResult.error}`}
          </div>
        )}
      </div>
      {showWalkthrough && <WalkthroughModal provider={provider} onClose={() => setShowWalkthrough(false)} />}
    </>
  );
}

export default function CreatorProviderByokPanel({ user, onUpdated }) {
  const savedCount = (user?.has_byok ? 1 : 0)
    + (user?.has_anthropic_byok ? 1 : 0)
    + (user?.has_openai_byok ? 1 : 0);
  return (
    <div id="creator-byok-providers-card" className="bg-white border hairline rounded-xl p-6 scroll-mt-28" data-testid="creator-byok-providers-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#FBE9E1] text-[#7A5200] flex items-center justify-center shrink-0">
          <Key size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-medium text-[#1C1B1A]">Bring your own LLM keys</h3>
            {savedCount === 3 && (
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#2F6B3A] bg-[#E8EBE5] px-2 py-0.5 rounded inline-flex items-center gap-1">
                <CheckCircle size={10} weight="duotone" /> All direct
              </span>
            )}
            {savedCount > 0 && savedCount < 3 && (
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#7A5200] bg-[#FBE9E1] px-2 py-0.5 rounded">
                {savedCount} of 3 direct
              </span>
            )}
          </div>
          <p className="text-xs text-[#6B6A68] mt-0.5 leading-relaxed">
            Save up to <strong>80% on LLM usage</strong> by connecting your own provider accounts. Each connected provider routes its calls directly to your account — the voucher only gets debited when the platform reservoir actually pays. Your voucher tracker turns <span className="text-[#2F6B3A] font-medium">green</span> for each connected provider.
          </p>
          <div className="mt-4 divide-y hairline">
            {PROVIDERS.map((p) => (
              <ProviderRow key={p.id} provider={p} user={user} onUpdated={onUpdated} />
            ))}
          </div>
          <p className="text-[11px] text-[#6B6A68] mt-4 leading-relaxed">
            <strong>Heads up:</strong> when a direct key is set, your silos charge YOUR provider account on every call. Enable auto-reload on each provider — a dead balance = silently broken silos.
          </p>
        </div>
      </div>
    </div>
  );
}
